import { decodeSounds, playSound, startSoundsOnPress } from "./sounds";

// The page's own pointer, a ring where each press lands, and a click when the press lands on
// a button. Components name the pointer through --cursor-default and --cursor-pointer,
// which inherit into every shadow root; the ring and the sound listen on the window, which every
// composed pointerdown reaches.

const ARROW_PATH = "M5 3.5v20.2l5.3-5.1 3.5 8.2 3.6-1.6-3.5-8h7.4z";

function cursorOf(fill: string, edge: string, hotspot: string, fallback: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">`
    + `<defs><linearGradient id="f" x1="0" y1="0" x2="0" y2="1">${fill}</linearGradient></defs>`
    + `<path d="${ARROW_PATH}" fill="url(#f)" stroke="${edge}" stroke-width="1.6" stroke-linejoin="round"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotspot}, ${fallback}`;
}

const ARROW_FILL = `<stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#d4d4d4"/>`;
const ARROW = cursorOf(ARROW_FILL, "#1b1b1b", "5 4", "default");
const ACTIONABLE_ARROW = cursorOf(ARROW_FILL, "#1b1b1b", "5 4", "pointer");

const PAGE_STYLES = `
  a[href], button:not(:disabled), summary { cursor: var(--cursor-pointer); }
  .click-ring {
    position: fixed; z-index: 2147483647; width: 34px; height: 34px; margin: -17px 0 0 -17px;
    border-radius: 50%; pointer-events: none; box-sizing: border-box;
    border: 2px solid rgb(201 153 230 / 0.55);
    animation: click-ring 420ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  .click-ring.acts { border-color: rgb(240 136 62 / 0.9); box-shadow: 0 0 12px rgb(240 136 62 / 0.45); }
  @keyframes click-ring {
    from { transform: scale(0.25); opacity: 1; }
    to { transform: scale(1); opacity: 0; }
  }
`;

function drawRing(x: number, y: number, acts: boolean): void {
  const ring = document.createElement("div");
  ring.className = acts ? "click-ring acts" : "click-ring";
  ring.style.left = `${x}px`;
  ring.style.top = `${y}px`;
  ring.addEventListener("animationend", () => ring.remove(), { once: true });
  document.body.append(ring);
}

function pressActs(e: PointerEvent): boolean {
  const target = e.composedPath()[0];
  return target instanceof Element && getComputedStyle(target).cursor.includes("pointer");
}

function pressesButton(e: PointerEvent): boolean {
  return e.composedPath().some((node) => node instanceof HTMLButtonElement && !node.disabled);
}

let installed = false;

export function installClickFeedback(): void {
  if (installed) return;
  installed = true;
  const root = document.documentElement;
  root.style.setProperty("--cursor-default", ARROW);
  root.style.setProperty("--cursor-pointer", ACTIONABLE_ARROW);
  root.style.cursor = "var(--cursor-default)";
  const style = document.createElement("style");
  style.textContent = PAGE_STYLES;
  document.head.append(style);
  decodeSounds();
  const calm = matchMedia("(prefers-reduced-motion: reduce)");
  window.addEventListener("pointerdown", (e) => {
    startSoundsOnPress();
    if (e.button !== 0) return;
    const acts = pressActs(e);
    if (!calm.matches) drawRing(e.clientX, e.clientY, acts);
    if (pressesButton(e)) playSound("click");
  }, { capture: true, passive: true });
  window.addEventListener("keydown", startSoundsOnPress, { capture: true, passive: true });
}
